import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { getOrganizationData, clearOrganizationCache, deleteOrganizationS3 } from '../services/organizationS3Service';
import './Dashboard.css';

interface DashboardProps {}

const Dashboard: React.FC<DashboardProps> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { orgId: urlOrgId } = useParams<{ orgId?: string }>();
  const [showOrgModal, setShowOrgModal] = useState(false);
  const [organizations, setOrganizations] = useState<Array<{id: string; name: string; asanaProject?: string; portfolios?: any[]}>>([]);
  const [portfolioOptions, setPortfolioOptions] = useState<Array<{key: string; label: string; orgId: string; portfolioId?: string}>>([]);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [orgToDelete, setOrgToDelete] = useState<{id: string; name: string} | null>(null);
  const [isLoadingOrgs, setIsLoadingOrgs] = useState(true);

  useEffect(() => {
    const loadOrganizations = async () => {
      try {
        setIsLoadingOrgs(true);
        // Use cached data to prevent continuous API calls
        const orgData = await getOrganizationData(false); // Don't force refresh
        if (orgData) {
          const orgs = orgData.organizations.map(org => ({ id: org.id, name: org.name, asanaProject: org.asanaProject, portfolios: (org as any).portfolios }));
          setOrganizations(orgs);
          
          // Build flattened portfolio options for the dropdown
          const options: Array<{key: string; label: string; orgId: string; portfolioId?: string}> = [];
          for (const org of orgs) {
            if (org.portfolios && org.portfolios.length > 0) {
              for (const p of org.portfolios) {
                options.push({
                  key: `${org.id}::${p.id}`,
                  label: orgs.length > 1 || org.portfolios.length > 1 ? `${org.name} → ${p.name}` : p.name,
                  orgId: org.id,
                  portfolioId: p.id,
                });
              }
            } else {
              options.push({ key: org.id, label: org.name, orgId: org.id });
            }
          }
          setPortfolioOptions(options);
        } else {
          // If no data available, set empty array to prevent continuous loading
          setOrganizations([]);
        }
      } catch (error) {
        console.error('Error loading organizations:', error);
        // Set empty array to prevent continuous retries
        setOrganizations([]);
      } finally {
        setIsLoadingOrgs(false);
      }
    };
    
    loadOrganizations();
  }, []); // Empty dependency array to run only once

  // Filter portfolio options to current org when org-scoped
  const filteredPortfolioOptions = urlOrgId 
    ? portfolioOptions.filter(opt => opt.orgId === urlOrgId)
    : portfolioOptions;

  const handlePlanningClick = () => {
    // If org-scoped and only one portfolio, go directly
    if (urlOrgId && filteredPortfolioOptions.length === 1) {
      const opt = filteredPortfolioOptions[0];
      navigate(opt.portfolioId 
        ? `/org/${opt.orgId}/tasks/${opt.portfolioId}` 
        : `/org/${opt.orgId}/tasks`);
      return;
    }
    if (filteredPortfolioOptions.length === 0) {
      alert('No portfolios found. Please create a portfolio in Organization Settings first.');
      return;
    }
    setShowOrgModal(true);
  };

  const handleOrgSelect = () => {
    if (!selectedOrgId) {
      alert('Please select a portfolio');
      return;
    }
    const selectedOption = filteredPortfolioOptions.find(opt => opt.key === selectedOrgId);
    if (!selectedOption) return;
    
    setShowOrgModal(false);
    if (selectedOption.portfolioId) {
      navigate(`/org/${selectedOption.orgId}/tasks/${selectedOption.portfolioId}`);
    } else {
      navigate(`/org/${selectedOption.orgId}/tasks`);
    }
  };

  const handleClearCache = async () => {
    clearOrganizationCache();
    // Reload organizations after clearing cache
    try {
      setIsLoadingOrgs(true);
      const orgData = await getOrganizationData(true);
      if (orgData) {
        setOrganizations(orgData.organizations.map(org => ({ id: org.id, name: org.name, asanaProject: org.asanaProject })));
      } else {
        setOrganizations([]);
      }
      alert('Cache cleared and data refreshed!');
    } catch (error) {
      console.error('Error refreshing data:', error);
      alert('Cache cleared, but failed to refresh data.');
    } finally {
      setIsLoadingOrgs(false);
    }
  };

  const handleDeleteOrg = (org: {id: string; name: string}) => {
    setOrgToDelete(org);
    setShowDeleteModal(true);
  };

  const confirmDeleteOrg = async () => {
    if (!orgToDelete) return;
    
    try {
      await deleteOrganizationS3(orgToDelete.id);
      // Refresh organizations list
      const orgData = await getOrganizationData(true);
      if (orgData) {
        setOrganizations(orgData.organizations.map(org => ({ id: org.id, name: org.name, asanaProject: org.asanaProject })));
      } else {
        setOrganizations([]);
      }
      alert(`Organization "${orgToDelete.name}" deleted successfully!`);
    } catch (error) {
      console.error('Error deleting organization:', error);
      alert('Failed to delete organization. Please try again.');
    } finally {
      setShowDeleteModal(false);
      setOrgToDelete(null);
    }
  };

  const handleCleanupFiles = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/organizations/cleanup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const result = await response.json();
      if (result.success) {
        alert(`Cleaned up ${result.data.deletedCount} orphaned files`);
      } else {
        alert('Failed to cleanup files');
      }
    } catch (error) {
      console.error('Error cleaning up files:', error);
      alert('Failed to cleanup files');
    }
  };

  const handleDebugOrgs = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/organizations/debug`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const result = await response.json();

      alert('Debug info logged to console. Check browser dev tools.');
    } catch (error) {
      console.error('Error debugging organizations:', error);
      alert('Failed to debug organizations');
    }
  };

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div style={{ display: 'flex', gap: '12px', position: 'absolute', top: '20px', left: '20px' }}>
          <button 
            onClick={() => navigate('/')}
            style={{
              padding: '8px 16px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5"/>
              <path d="M12 19l-7-7 7-7"/>
            </svg>
            Home
          </button>
          {urlOrgId && (
            <button 
              onClick={() => navigate(`/org/${urlOrgId}`)}
              style={{
                padding: '8px 16px',
                backgroundColor: '#38a169',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              💬 Back to Chat
            </button>
          )}
        </div>
        <h1 className="dashboard-title">
          {urlOrgId ? `${organizations.find(o => o.id === urlOrgId)?.name || ''} — Filly Hub` : 'Product Management Hub'}
        </h1>
        <p className="dashboard-subtitle">
          OP HC and product planning, and request grooming
        </p>
        {/* <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
          <button 
            onClick={handleClearCache}
            style={{
              padding: '8px 16px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Clear Cache
          </button>
          <button 
            onClick={handleCleanupFiles}
            style={{
              padding: '8px 16px',
              backgroundColor: '#ffc107',
              color: 'black',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Cleanup Files
          </button>
          <button 
            onClick={handleDebugOrgs}
            style={{
              padding: '8px 16px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Debug Orgs
          </button>
          {isLoadingOrgs ? (
            <div style={{
              padding: '8px 16px',
              backgroundColor: '#f8f9fa',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid #007bff',
                borderTop: '2px solid transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              Loading organizations...
            </div>
          ) : organizations.length > 0 && (
            <select 
              onChange={(e) => {
                if (e.target.value) {
                  const org = organizations.find(o => o.id === e.target.value);
                  if (org) handleDeleteOrg(org);
                  e.target.value = '';
                }
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              <option value="">Delete Organization</option>
              {organizations.map(org => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </select>
          )}
        </div> */}
      </div>

 

      <div className="dashboard-grid">
        {/* Organization Management */}
        <div className="dashboard-card" onClick={() => navigate(urlOrgId ? `/org/${urlOrgId}/settings` : '/admin/organization')}>
          <div className="card-icon organization-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
          </div>
          <div className="card-content">
            <h3 className="card-title">Portfolio Management</h3>
            <p className="card-description">
              Set strategy, manage headcount planning, and oversee team structure
            </p>
            <ul className="card-features">
              <li>Strategic planning & roadmaps</li>
              <li>Headcount allocation & forecasting</li>
              <li>Team structure & analytics</li>
            </ul>
          </div>
          <div className="card-action">
            <span className="action-text">Manage Organization →</span>
          </div>
        </div>

        {/* OP Planning */}
        <div className="dashboard-card" onClick={handlePlanningClick}>
          <div className="card-icon planning-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
              <line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/>
              <line x1="3" y1="10" x2="21" y2="10"/>
              <path d="M8 14h.01"/>
              <path d="M12 14h.01"/>
              <path d="M16 14h.01"/>
              <path d="M8 18h.01"/>
              <path d="M12 18h.01"/>
              <path d="M16 18h.01"/>
            </svg>
          </div>
          <div className="card-content">
            <h3 className="card-title">OP Planning</h3>
            <p className="card-description">
              Manage operational planning cycles, prioritization, and resource allocation
            </p>
            <ul className="card-features">
              <li>Task prioritization & scheduling</li>
              <li>Resource allocation tracking</li>
              <li>Planning analytics & insights</li>
            </ul>
          </div>
          <div className="card-action">
            <span className="action-text">Start Planning →</span>
          </div>
        </div>

    
      </div>

      {/* Organization Selection Modal */}
      {showOrgModal && (
        <div className="modal-overlay" onClick={() => setShowOrgModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Select a Portfolio</h3>
              <button className="modal-close" onClick={() => setShowOrgModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '16px', color: '#666' }}>Choose a portfolio to plan for:</p>
              {isLoadingOrgs ? (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '20px',
                  gap: '10px'
                }}>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    border: '2px solid #007bff',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  Loading organizations...
                </div>
              ) : (
                <select 
                  value={selectedOrgId} 
                  onChange={(e) => setSelectedOrgId(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    fontSize: '14px',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                >
                  <option value="">-- Select Portfolio --</option>
                  {filteredPortfolioOptions.map((opt, idx) => (
                    <option key={idx} value={opt.key}>{opt.label}</option>
                  ))}
                </select>
              )}
            </div>
            <div className="modal-footer" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button 
                onClick={() => setShowOrgModal(false)}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  background: 'white',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button 
                onClick={handleOrgSelect}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '4px',
                  background: '#007bff',
                  color: 'white',
                  cursor: 'pointer'
                }}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Organization Confirmation Modal */}
      {showDeleteModal && orgToDelete && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete Organization</h3>
              <button className="modal-close" onClick={() => setShowDeleteModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '16px', color: '#666' }}>
                Are you sure you want to delete the organization <strong>"{orgToDelete.name}"</strong>?
              </p>
              <p style={{ color: '#dc3545', fontSize: '14px' }}>
                This action cannot be undone and will permanently remove all organization data.
              </p>
            </div>
            <div className="modal-footer" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button 
                onClick={() => setShowDeleteModal(false)}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  background: 'white',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button 
                onClick={confirmDeleteOrg}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '4px',
                  background: '#dc3545',
                  color: 'white',
                  cursor: 'pointer'
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
